import { TemplateRef, Type } from '@angular/core';
import {
  ColumnDef,
  ColumnFiltersState,
  ColumnOrderState,
  ColumnPinningState,
  ColumnSizingState,
  ExpandedState,
  GroupingState,
  PaginationState,
  RowPinningState,
  RowSelectionState,
  SortingState,
  VisibilityState,
} from '@tanstack/angular-table';

/**
 * Icon content type - can be a string (HTML/Unicode), TemplateRef, or Component
 * This is compatible with FlexRender for unified rendering
 */
export type IconContent = string | TemplateRef<unknown> | Type<unknown>;

/**
 * Complete icon set for the table component
 * All icons can be customized by providing partial overrides
 */
export interface TableIconSet {
  // Sorting icons
  sortAsc: IconContent;
  sortDesc: IconContent;
  multiSortAsc: IconContent;
  multiSortDesc: IconContent;

  // Filter icons
  filter: IconContent;
  filterActive: IconContent;

  // Menu icons
  menu: IconContent;
  menuClose: IconContent;

  // Pagination icons
  firstPage: IconContent;
  previousPage: IconContent;
  nextPage: IconContent;
  lastPage: IconContent;

  // Expand/collapse icons
  expand: IconContent;
  collapse: IconContent;

  // Grouping icons
  groupBy: IconContent;
  ungroup: IconContent;

  // Column pinning icons (direction-agnostic)
  pinStart: IconContent;
  pinEnd: IconContent;
  // Legacy column pinning icons (kept for backwards compatibility)
  pinLeft: IconContent;
  pinRight: IconContent;
  unpin: IconContent;

  // Row pinning icons
  pinTop: IconContent;
  pinBottom: IconContent;
  pinned: IconContent;

  // Action icons
  clear: IconContent;
  hide: IconContent;
  close: IconContent;
}

/**
 * Available built-in themes
 */
export type TableTheme = 'none' | 'material' | string;

/**
 * Theme configuration for programmatic theming
 */
export interface TableThemeConfig {
  // Colors
  background?: string;
  foreground?: string;
  primary?: string;
  secondary?: string;
  border?: string;
  headerBackground?: string;
  headerForeground?: string;
  rowHoverBackground?: string;
  rowSelectedBackground?: string;

  // Typography
  fontFamily?: string;
  fontSize?: string;
  headerFontSize?: string;
  fontWeight?: string;
  headerFontWeight?: string;

  // Spacing
  cellPadding?: string;
  headerPadding?: string;
  toolbarPadding?: string;

  // Dimensions
  rowHeight?: string;
  headerHeight?: string;
  borderWidth?: string;
  borderRadius?: string;

  // Effects
  boxShadow?: string;
  transition?: string;
}

/**
 * Complete table state interface
 */
export interface TableState {
  sorting: SortingState;
  columnFilters: ColumnFiltersState;
  globalFilter: string;
  pagination: PaginationState;
  rowSelection: RowSelectionState;
  columnVisibility: VisibilityState;
  columnOrder: ColumnOrderState;
  columnPinning: ColumnPinningState;
  columnSizing: ColumnSizingState;
  expanded: ExpandedState;
  grouping: GroupingState;
  rowPinning: RowPinningState;
}

/**
 * Table configuration options
 */
export interface TableConfig<TData> {
  // Data
  data: TData[];
  columns: ColumnDef<TData, any>[];

  // Feature flags
  enableSorting?: boolean;
  enableMultiSort?: boolean;
  enableFiltering?: boolean;
  enableGlobalFilter?: boolean;
  enablePagination?: boolean;
  enableRowSelection?: boolean;
  enableMultiRowSelection?: boolean;
  enableColumnResizing?: boolean;
  enableColumnOrdering?: boolean;
  enableColumnPinning?: boolean;
  enableColumnVisibility?: boolean;
  enableGrouping?: boolean;
  enableExpanding?: boolean;
  enableRowPinning?: boolean;
  enableVirtualization?: boolean;

  // Column sizing behavior
  /** When true, columns will auto-fit to fill available width on initial render, then use fixed widths for resizing */
  initialAutoFitColumns?: boolean;

  // Pagination config
  initialPageSize?: number;
  pageSizeOptions?: number[];

  // Callbacks
  onRowSelectionChange?: (selectedRows: TData[]) => void;
  onStateChange?: (state: Partial<TableState>) => void;

  // Sub-row configuration
  getSubRows?: (originalRow: TData, index: number) => TData[] | undefined;

  // Row ID
  getRowId?: (originalRow: TData, index: number) => string;

  // Initial state
  initialState?: Partial<TableState>;
}

/**
 * Pagination info for display
 */
export interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalRows: number;
  startRow: number;
  endRow: number;
  canPreviousPage: boolean;
  canNextPage: boolean;
}

/**
 * Column menu actions
 */
export interface ColumnMenuAction {
  label: string;
  icon?: string;
  action: () => void;
  disabled?: boolean;
}

/**
 * Filter operator types
 */
export type FilterOperator =
  | 'equals'
  | 'notEquals'
  | 'contains'
  | 'notContains'
  | 'startsWith'
  | 'endsWith'
  | 'greaterThan'
  | 'greaterThanOrEqual'
  | 'lessThan'
  | 'lessThanOrEqual'
  | 'between'
  | 'isEmpty'
  | 'isNotEmpty';

/**
 * Column filter config
 */
export interface ColumnFilterConfig {
  columnId: string;
  operator: FilterOperator;
  value: any;
}

/**
 * Aggregation function type
 */
export type AggregationFn = 'sum' | 'min' | 'max' | 'count' | 'mean' | 'median' | 'unique';

/**
 * Export format options
 */
export type ExportFormat = 'csv' | 'json' | 'excel';

/**
 * All translatable labels in the table component
 */
export interface TableLabels {
  // Toolbar
  searchPlaceholder: string;
  columnsButton: string;
  clearFilters: string;
  clearSorting: string;
  clearGrouping: string;
  clearColumnPinning: string;
  clearRowPinning: string;
  groupedBy: string;

  // No data
  noDataAvailable: string;

  // Column menu
  columnMenuTitle: string;
  sortAscending: string;
  sortDescending: string;
  addSortAscending: string;
  addSortDescending: string;
  clearSort: string;
  removeGrouping: string;
  groupBy: string;
  pinLeft: string;
  pinRight: string;
  unpin: string;
  hideColumn: string;
  clearFilter: string;
  resetSection: string;
  clearAllFilters: string;
  clearAllSorting: string;
  clearAllGrouping: string;
  clearAllPinning: string;

  // Filter popover
  filterTitle: string;
  closeTitle: string;
  operatorLabel: string;
  valueLabel: string;
  filterPlaceholder: string;
  clearButton: string;
  doneButton: string;

  // Filter operators
  operatorContains: string;
  operatorNotContains: string;
  operatorEquals: string;
  operatorNotEquals: string;
  operatorStartsWith: string;
  operatorEndsWith: string;
  operatorGreaterThan: string;
  operatorGreaterThanOrEqual: string;
  operatorLessThan: string;
  operatorLessThanOrEqual: string;
  operatorIsEmpty: string;
  operatorIsNotEmpty: string;

  // Row pinning
  unpinRowTitle: string;
  pinToTopTitle: string;
  pinToBottomTitle: string;

  // Pagination
  firstPageTitle: string;
  previousPageTitle: string;
  nextPageTitle: string;
  lastPageTitle: string;
  showingRows: string; // "Showing {start} to {end} of {total} rows"
  pageInfo: string; // "Page {current} of {total}"
  rowsPerPage: string;
}

/**
 * Default English labels
 */
export const DEFAULT_TABLE_LABELS: TableLabels = {
  // Toolbar
  searchPlaceholder: 'Search all columns...',
  columnsButton: 'Columns',
  clearFilters: 'Clear Filters',
  clearSorting: 'Clear Sorting',
  clearGrouping: 'Clear Grouping',
  clearColumnPinning: 'Clear Column Pinning',
  clearRowPinning: 'Clear Row Pinning',
  groupedBy: 'Grouped by:',

  // No data
  noDataAvailable: 'No data available',

  // Column menu
  columnMenuTitle: 'Column menu',
  sortAscending: 'Sort Ascending',
  sortDescending: 'Sort Descending',
  addSortAscending: 'Add Sort Ascending',
  addSortDescending: 'Add Sort Descending',
  clearSort: 'Clear Sort',
  removeGrouping: 'Remove Grouping',
  groupBy: 'Group By',
  pinLeft: 'Pin Left',
  pinRight: 'Pin Right',
  unpin: 'Unpin',
  hideColumn: 'Hide Column',
  clearFilter: 'Clear Filter',
  resetSection: 'Reset',
  clearAllFilters: 'Clear All Filters',
  clearAllSorting: 'Clear All Sorting',
  clearAllGrouping: 'Clear All Grouping',
  clearAllPinning: 'Clear All Pinning',

  // Filter popover
  filterTitle: 'Filter',
  closeTitle: 'Close',
  operatorLabel: 'Operator',
  valueLabel: 'Value',
  filterPlaceholder: 'Enter filter value...',
  clearButton: 'Clear',
  doneButton: 'Done',

  // Filter operators
  operatorContains: 'Contains',
  operatorNotContains: 'Not Contains',
  operatorEquals: 'Equals',
  operatorNotEquals: 'Not Equals',
  operatorStartsWith: 'Starts With',
  operatorEndsWith: 'Ends With',
  operatorGreaterThan: 'Greater Than',
  operatorGreaterThanOrEqual: 'Greater Than or Equal',
  operatorLessThan: 'Less Than',
  operatorLessThanOrEqual: 'Less Than or Equal',
  operatorIsEmpty: 'Is Empty',
  operatorIsNotEmpty: 'Is Not Empty',

  // Row pinning
  unpinRowTitle: 'Unpin row',
  pinToTopTitle: 'Pin to top',
  pinToBottomTitle: 'Pin to bottom',

  // Pagination
  firstPageTitle: 'First page',
  previousPageTitle: 'Previous page',
  nextPageTitle: 'Next page',
  lastPageTitle: 'Last page',
  showingRows: 'Showing {start} to {end} of {total} rows',
  pageInfo: 'Page {current} of {total}',
  rowsPerPage: 'Rows per page:',
};

/**
 * Arabic labels (RTL)
 */
export const ARABIC_TABLE_LABELS: TableLabels = {
  // Toolbar
  searchPlaceholder: 'البحث في جميع الأعمدة...',
  columnsButton: 'الأعمدة',
  clearFilters: 'مسح الفلاتر',
  clearSorting: 'مسح الترتيب',
  clearGrouping: 'مسح التجميع',
  clearColumnPinning: 'مسح تثبيت الأعمدة',
  clearRowPinning: 'مسح تثبيت الصفوف',
  groupedBy: 'مجمّع حسب:',

  // No data
  noDataAvailable: 'لا توجد بيانات',

  // Column menu
  columnMenuTitle: 'قائمة العمود',
  sortAscending: 'ترتيب تصاعدي',
  sortDescending: 'ترتيب تنازلي',
  addSortAscending: 'إضافة ترتيب تصاعدي',
  addSortDescending: 'إضافة ترتيب تنازلي',
  clearSort: 'مسح الترتيب',
  removeGrouping: 'إزالة التجميع',
  groupBy: 'تجميع حسب',
  pinLeft: 'تثبيت يسار',
  pinRight: 'تثبيت يمين',
  unpin: 'إلغاء التثبيت',
  hideColumn: 'إخفاء العمود',
  clearFilter: 'مسح الفلتر',
  resetSection: 'إعادة تعيين',
  clearAllFilters: 'مسح جميع الفلاتر',
  clearAllSorting: 'مسح جميع الترتيب',
  clearAllGrouping: 'مسح جميع التجميع',
  clearAllPinning: 'مسح جميع التثبيت',

  // Filter popover
  filterTitle: 'فلتر',
  closeTitle: 'إغلاق',
  operatorLabel: 'المعامل',
  valueLabel: 'القيمة',
  filterPlaceholder: 'أدخل قيمة الفلتر...',
  clearButton: 'مسح',
  doneButton: 'تم',

  // Filter operators
  operatorContains: 'يحتوي على',
  operatorNotContains: 'لا يحتوي على',
  operatorEquals: 'يساوي',
  operatorNotEquals: 'لا يساوي',
  operatorStartsWith: 'يبدأ بـ',
  operatorEndsWith: 'ينتهي بـ',
  operatorGreaterThan: 'أكبر من',
  operatorGreaterThanOrEqual: 'أكبر من أو يساوي',
  operatorLessThan: 'أصغر من',
  operatorLessThanOrEqual: 'أصغر من أو يساوي',
  operatorIsEmpty: 'فارغ',
  operatorIsNotEmpty: 'غير فارغ',

  // Row pinning
  unpinRowTitle: 'إلغاء تثبيت الصف',
  pinToTopTitle: 'تثبيت في الأعلى',
  pinToBottomTitle: 'تثبيت في الأسفل',

  // Pagination
  firstPageTitle: 'الصفحة الأولى',
  previousPageTitle: 'الصفحة السابقة',
  nextPageTitle: 'الصفحة التالية',
  lastPageTitle: 'الصفحة الأخيرة',
  showingRows: 'عرض {start} إلى {end} من {total} صف',
  pageInfo: 'صفحة {current} من {total}',
  rowsPerPage: 'صفوف في الصفحة:',
};
